import { memo, useCallback, useMemo } from "react"
import {
    CreateUpdateMeetingServiceOrder,
    CreateOrderDialogFlow,
    CreateOrderFlowType,
    CreateOrderDialogType,
} from "../../createOrderTypes"
import { FinalSuccessDialogContent } from "./FinalSuccessDialogContent"
import { OrderFormStatus } from "../../mainForm/helpers"
import { CreateOrderType } from "../../helpers"

type FinalSuccessDialogMeetingServiceProps = Readonly<{
    orderDetails: CreateUpdateMeetingServiceOrder
    onClose: () => void
    handleCloseMainDialog: () => void
    handleAddNewMeetingService: () => void
    handleOpenResourceBooking: (type: CreateOrderType) => void
    flowConfig?: CreateOrderDialogFlow
    orderFormStatusRef: React.MutableRefObject<OrderFormStatus>
    dialogType: CreateOrderDialogType
}>

export const FinalSuccessDialogMeetingService = memo(function FinalSuccessDialogMeetingService({
    orderDetails,
    onClose,
    handleCloseMainDialog,
    handleAddNewMeetingService,
    handleOpenResourceBooking,
    flowConfig,
    orderFormStatusRef,
    dialogType,
}: FinalSuccessDialogMeetingServiceProps) {
    const { isFullInitOrder, resourceBookingId } = orderFormStatusRef.current
    const isOutlookFlow = flowConfig?.flow === CreateOrderFlowType.OUTLOOK
    const isSpecialDialog = dialogType !== CreateOrderDialogType.ORDER
    const hasVstoFlowParam = Boolean(flowConfig?.vstoFlowParam)

    // rules:
    // 1) if isSpecialDialog — both actions are disabled
    // 2) if isOutlookFlow — do not show “New service”
    // 3) if vstoFlowParam exists — do not show “Add resource booking”
    const canShowResourceBooking = !isSpecialDialog && !hasVstoFlowParam
    const canShowNewService = !isSpecialDialog && !isOutlookFlow

    const wrapWithClose = useCallback(
        (fn: () => void) => () => {
            onClose()
            fn()
        },
        [onClose],
    )

    const handleFinish = useMemo(() => wrapWithClose(handleCloseMainDialog), [wrapWithClose, handleCloseMainDialog])

    const handleAddResourceBooking = useMemo(
        () =>
            canShowResourceBooking
                ? wrapWithClose(() => handleOpenResourceBooking(CreateOrderType.RESOURCE_BOOKING))
                : undefined,
        [canShowResourceBooking, wrapWithClose, handleOpenResourceBooking],
    )

    const handleNewService = useMemo(
        () => (canShowNewService ? wrapWithClose(handleAddNewMeetingService) : undefined),
        [canShowNewService, wrapWithClose, handleAddNewMeetingService],
    )

    return (
        <FinalSuccessDialogContent
            open={Boolean(orderDetails)}
            onClose={onClose}
            handleFinish={handleFinish}
            handleAddResourceBooking={handleAddResourceBooking}
            handleNewService={handleNewService}
            hideClose
            isUpdate={isFullInitOrder || !!resourceBookingId}
            isOutlookFlow={isOutlookFlow}
        />
    )
})
